import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import Router, { useRouter } from 'next/router';
import {
  faFileArrowUp,
  faListMusic,
  faPenToSquare,
  faPlus,
  type IconDefinition,
} from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@components/basics/Dropdowns/GlobalDropdown';
import { Spinner } from '@components/basics/Spinner';
import { Button } from '@components/main-components/button/Button';
import { IconButton } from '@components/main-components/icon-buttons/IconButton';
import { FeedPostModal } from '@components/modals/FeedPostModal';
import { UnlockFeedModal } from '@components/modals/FeedPostModal/UnlockFeedModal';
import { useAuth } from '@context/auth/base';
import { useConnectedAuth } from '@context/auth/connected';
import { ModalType, useModal } from '@context/modal';
import { useBackendUserPersistedData } from '@hooks/useBackendUserPersistedData';
import { useTieredUpload } from '@hooks/useTieredUpload';
import { NAV_LINKS } from '@utils/constants';

const CreateItem = ({
  icon,
  label,
  onClick,
  loading,
}: {
  icon: IconDefinition;
  label: string;
  onClick?: () => void;
  loading?: boolean;
}) => (
  <div
    className="flex h-[38px] cursor-pointer items-center gap-1 rounded-full px-2 py-1 transition-colors duration-300 hover:bg-base100"
    onClick={onClick}
  >
    <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center [&_svg]:h-[20px]">
      {loading ? <Spinner /> : <FontAwesomeIcon icon={icon} className="text-base800" />}
    </div>
    <span className="font-title text-title-xs font-medium text-base800">{label}</span>
  </div>
);

export const CreateButton = ({
  onlyIcon,
  navRef,
}: {
  onlyIcon?: boolean;
  navRef?: React.RefObject<HTMLDivElement>;
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const { createDraft, loading } = useTieredUpload();
  const { authUser, isAuthenticatedWithAPI } = useAuth();
  const { login } = useConnectedAuth();
  const isVerifiedArtist = !!authUser?.artist;
  const { pathname } = useRouter();

  const isFeedPage = pathname === NAV_LINKS.Feed;
  const timeoutMs = isFeedPage ? 0 : 1000;

  const { hasFeedAccess } = useBackendUserPersistedData();
  const { openModal } = useModal();

  const hasPostingAccess = hasFeedAccess.type === 'has-access';

  const onCreatePostClick = useStableCallback(() => {
    if (!isAuthenticatedWithAPI) {
      Router.push(NAV_LINKS.Feed);
      closeDropdown();
      return;
    }
    Router.push(NAV_LINKS.Feed);
    if (hasPostingAccess) {
      setTimeout(() => {
        openModal(ModalType.FEED_POST, {
          body: (
            <FeedPostModal
              key="post-input"
              sourceLocation="Feed Post Input"
              preselectedResource={null}
            />
          ),
        });
      }, timeoutMs);
    } else {
      setTimeout(() => {
        openModal(ModalType.UNLOCK_FEED_POST, {
          body: (
            <UnlockFeedModal
              key="unlock-feed-post"
              sourceLocation="Feed Post Input"
              preselectedResource={null}
            />
          ),
        });
      }, timeoutMs);
    }

    closeDropdown();
  });

  const onCreatePlaylistClick = useStableCallback(() => {
    if (!isAuthenticatedWithAPI) {
      login();
      return;
    } else {
      Router.push(NAV_LINKS.CreatePlaylist);
    }
    closeDropdown();
  });

  const onUploadClick = async () => {
    if (isVerifiedArtist) {
      // This is already handling errors
      await createDraft();
    } else {
      Router.push(NAV_LINKS.ArtistApplication);
    }
    closeDropdown();
  };

  const onOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      navRef?.current?.classList.add('create-open');
    } else {
      navRef?.current?.classList.remove('create-open');
    }
    setIsDropdownOpen(isOpen);
  };

  const closeDropdown = () => {
    navRef?.current?.classList.remove('create-open');
    setIsDropdownOpen(false);
  };

  return (
    <DropdownMenu onOpenChange={onOpenChange} open={isDropdownOpen}>
      <DropdownMenuTrigger className="outline-0" asChild>
        <div>
          <div className={clsx(onlyIcon ? 'hidden' : 'flex', 'pt-3')}>
            <Button
              variant="primary"
              label="Create"
              leadingIcon={
                <FontAwesomeIcon
                  icon={faPlus}
                  className={clsx(
                    isDropdownOpen && 'rotate-45 transform',
                    !isDropdownOpen && 'rotate-0 transform',
                    'transition-all duration-300',
                  )}
                />
              }
              fullWidth
              className="rounded-full"
            />
          </div>
          <div className={clsx(onlyIcon ? 'flex' : 'hidden', 'pt-3')}>
            <IconButton
              icon={
                <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center [&_svg]:h-[16px]">
                  <FontAwesomeIcon
                    icon={faPlus}
                    className={clsx(
                      isDropdownOpen && 'rotate-45 transform',
                      !isDropdownOpen && 'rotate-0 transform',
                      'text-white transition-all duration-300',
                    )}
                  />
                </div>
              }
              className="rounded-full bg-base800"
            />
          </div>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="z-feedPostFloatingButton w-52 rounded-xl border-0 p-4"
        align="center"
        onFocusOutside={closeDropdown}
      >
        <div className="relative flex flex-col gap-2 rounded-xl bg-white">
          <CreateItem label="Create post" icon={faPenToSquare} onClick={onCreatePostClick} />
          <CreateItem label="Create playlist" icon={faListMusic} onClick={onCreatePlaylistClick} />
          <CreateItem
            label="Upload sound"
            icon={faFileArrowUp}
            onClick={onUploadClick}
            loading={loading}
          />
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
